import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo } from 'react';

import type { PrefetchHandlers } from '@/lib/hooks/usePrefetchOnIntent';
import { usePrefetchOnIntent } from '@/lib/hooks/usePrefetchOnIntent';
import { logger } from '@/lib/logger';
import type { CrmObject } from '@/types/crm';

import { createResearchQueryConfig } from '../components/utils/meetingResearchQuery';

/** Check if error is recoverable (network/server errors) */
export function isRecoverableError(error: unknown): boolean {
  if (error instanceof TypeError) {
    return true;
  }
  if (error && typeof error === 'object') {
    // Check both `status` and `statusCode` — API errors may use either property
    const statusCode =
      ('statusCode' in error && typeof error.statusCode === 'number'
        ? error.statusCode
        : undefined) ??
      ('status' in error && typeof error.status === 'number'
        ? error.status
        : undefined);
    if (statusCode !== undefined && statusCode >= 500) {
      return true;
    }
  }
  return false;
}

export interface UseMeetingResearchResult {
  data: CrmObject[] | undefined;
  isLoading: boolean;
  error: unknown;
  prefetchHandlers: PrefetchHandlers;
}

/**
 * Fetches a meeting's research data (prep briefs) and provides hover-intent
 * prefetching for it.
 *
 * Extracted out of `MeetingRow` to separate data fetching from row rendering:
 * `MeetingRow` is a 900-line component and the research query, its prefetch
 * callback, and its error-logging effect were three cooperating pieces of
 * fetch orchestration living inline among unrelated rendering concerns. They
 * move together because they share the same query config and query key —
 * splitting them further would just duplicate `createResearchQueryConfig`
 * calls.
 *
 * The retry policy intentionally treats only recoverable errors (network
 * failures, 5xx) as retryable — a 403/404 retrying three times just delays
 * showing the user a message they can't act on.
 */
export function useMeetingResearch(
  orgId: number,
  meetingEntityId: string,
  isExpanded: boolean
): UseMeetingResearchResult {
  const queryClient = useQueryClient();

  const prefetchHandlers = usePrefetchOnIntent(
    useCallback(async () => {
      if (!meetingEntityId) {
        return;
      }
      await queryClient.prefetchQuery(
        createResearchQueryConfig(orgId, meetingEntityId)
      );
    }, [meetingEntityId, orgId, queryClient])
  );

  const { data, isLoading, error } = useQuery({
    ...createResearchQueryConfig(orgId, meetingEntityId),
    enabled: isExpanded && !!meetingEntityId,
    retry: (failureCount, error) =>
      failureCount < 3 && isRecoverableError(error),
  });

  // Log errors for observability
  useEffect(() => {
    if (error) {
      const normalizedError =
        error instanceof Error ? error : new Error(String(error));
      logger.error('Failed to fetch meeting research', normalizedError, {
        component: 'MeetingRow',
        meetingEntityId,
        orgId,
        originalErrorType: error?.constructor?.name || typeof error,
        originalError: String(error),
      });
    }
  }, [error, meetingEntityId, orgId]);

  return useMemo(
    () => ({ data, isLoading, error, prefetchHandlers }),
    [data, isLoading, error, prefetchHandlers]
  );
}
